import { useState, useRef } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Upload,
  X,
  CheckCircle,
  AlertCircle,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_area: "",
    property_type: "",
    service_needed: "",
    message: "",
    preferred_date: "",
  });
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }

    files.forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 5MB per image.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setImages((prev) => [
          ...prev,
          {
            name: file.name,
            preview: event.target.result,
            base64: event.target.result,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        images: images.map((img) => img.base64),
      };

      await axios.post(`${API}/contact`, payload);
      setSubmitted(true);
      toast.success("Your request has been submitted! We'll contact you shortly.");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        id="contact"
        className="py-28 lg:py-36 bg-gradient-to-br from-[#0A1628] to-[#1E3A5F]"
        data-testid="contact-section-success"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white p-12 shadow-2xl">
            <div className="w-20 h-20 bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#0A1628] mb-4">
              Thank You!
            </h2>
            <p className="text-[#64748B] mb-8 text-lg">
              Your request has been received. One of our ISA Certified Arborists
              will contact you within 24 hours to discuss your tree care needs.
            </p>
            <p className="text-[#10B981] font-bold mb-10 text-lg">
              Need immediate assistance? Call (480) 351-7775
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  name: "",
                  email: "",
                  phone: "",
                  service_area: "",
                  property_type: "",
                  service_needed: "",
                  message: "",
                  preferred_date: "",
                });
                setImages([]);
              }}
              className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-10 py-5 uppercase tracking-[0.15em] text-sm font-bold"
            >
              Submit Another Request
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="contact"
      className="contact-section py-28 lg:py-36"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
              Get In Touch
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-white mb-8 leading-tight">
              Ready to Protect
              <br />
              <span className="gradient-text">Your Property?</span>
            </h2>
            <p className="text-white/70 text-lg mb-10 leading-relaxed">
              Contact us today for a free assessment. Our ISA Certified
              Arborists are standing by to help protect and beautify your
              landscape.
            </p>

            {/* Contact Details */}
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white/50 text-sm mb-1">Phone</p>
                  <a
                    href="tel:4803517775"
                    className="text-white text-xl font-bold hover:text-[#10B981] transition-colors"
                  >
                    (480) 351-7775
                  </a>
                  <p className="text-[#10B981] text-sm mt-1 font-semibold">
                    24/7 Emergency Available
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="w-14 h-14 bg-[#10B981]/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm mb-1">Email</p>
                  <a
                    href="mailto:info@paradisevalleytreeservice.com"
                    className="text-white text-lg font-semibold hover:text-[#10B981] transition-colors"
                  >
                    info@paradisevalleytreeservice.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="w-14 h-14 bg-[#10B981]/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm mb-1">Service Areas</p>
                  <p className="text-white text-lg font-semibold">
                    Paradise Valley & Scottsdale, AZ
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="w-14 h-14 bg-[#10B981]/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm mb-1">Business Hours</p>
                  <p className="text-white">Mon - Fri: 7:00 AM - 6:00 PM</p>
                  <p className="text-white">Saturday: 8:00 AM - 4:00 PM</p>
                  <p className="text-[#DC2626] font-semibold">
                    Sunday: Emergency Service Only
                  </p>
                </div>
              </div>
            </div>

            {/* Emergency Banner */}
            <div className="bg-[#DC2626]/20 border border-[#DC2626]/40 p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-[#DC2626] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-bold mb-1">
                    24/7 Storm & Monsoon Emergency Support
                  </p>
                  <p className="text-white/70 text-sm">
                    Damage from a storm? We're here around the clock during
                    monsoon season. Call our emergency line immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-10 shadow-2xl">
            <div className="flex items-center gap-2 mb-6">
              <Star className="w-5 h-5 text-[#D4A853]" fill="#D4A853" />
              <Star className="w-5 h-5 text-[#D4A853]" fill="#D4A853" />
              <Star className="w-5 h-5 text-[#D4A853]" fill="#D4A853" />
              <Star className="w-5 h-5 text-[#D4A853]" fill="#D4A853" />
              <Star className="w-5 h-5 text-[#D4A853]" fill="#D4A853" />
              <span className="text-[#64748B] text-sm ml-2">5-Star Rated Service</span>
            </div>
            
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#0A1628] mb-8">
              Request Your Free Assessment
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                    Your Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="luxury-input border-0 border-b-2 border-[#10B981]/30 rounded-none focus:border-[#10B981] focus-visible:ring-0 bg-[#F8F6F1]/50"
                    placeholder="John Smith"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                    Phone *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="luxury-input border-0 border-b-2 border-[#10B981]/30 rounded-none focus:border-[#10B981] focus-visible:ring-0 bg-[#F8F6F1]/50"
                    placeholder="(480) 555-1234"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                  Email *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="luxury-input border-0 border-b-2 border-[#10B981]/30 rounded-none focus:border-[#10B981] focus-visible:ring-0 bg-[#F8F6F1]/50"
                  placeholder="john@example.com"
                  data-testid="contact-email-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                    Service Area *
                  </label>
                  <Select
                    onValueChange={(value) =>
                      handleSelectChange("service_area", value)
                    }
                    data-testid="contact-service-area-select"
                  >
                    <SelectTrigger className="border-0 border-b-2 border-[#10B981]/30 rounded-none focus:ring-0 bg-[#F8F6F1]/50">
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paradise-valley">
                        Paradise Valley
                      </SelectItem>
                      <SelectItem value="scottsdale">Scottsdale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                    Property Type *
                  </label>
                  <Select
                    onValueChange={(value) =>
                      handleSelectChange("property_type", value)
                    }
                    data-testid="contact-property-type-select"
                  >
                    <SelectTrigger className="border-0 border-b-2 border-[#10B981]/30 rounded-none focus:ring-0 bg-[#F8F6F1]/50">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="hoa">HOA Community</SelectItem>
                      <SelectItem value="hotel">Hotel/Resort</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="golf-course">Golf Course</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                  Service Needed *
                </label>
                <Select
                  onValueChange={(value) =>
                    handleSelectChange("service_needed", value)
                  }
                  data-testid="contact-service-needed-select"
                >
                  <SelectTrigger className="border-0 border-b-2 border-[#10B981]/30 rounded-none focus:ring-0 bg-[#F8F6F1]/50">
                    <SelectValue placeholder="What do you need help with?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tree-trimming">Tree Trimming</SelectItem>
                    <SelectItem value="tree-removal">Tree Removal</SelectItem>
                    <SelectItem value="stump-grinding">Stump Grinding</SelectItem>
                    <SelectItem value="fire-safety">Fire Safety & Brush Removal</SelectItem>
                    <SelectItem value="arborist-assessment">ISA Arborist Assessment (Paid Service)</SelectItem>
                    <SelectItem value="emergency">Emergency Service</SelectItem>
                    <SelectItem value="maintenance-contract">Maintenance Contract (Commercial)</SelectItem>
                    <SelectItem value="golf-course-care">Golf Course Lawn Care</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Arborist Notice */}
                {formData.service_needed === "arborist-assessment" && (
                  <div className="mt-3 p-3 bg-[#D4A853]/10 border border-[#D4A853]/30 text-sm">
                    <p className="text-[#0A1628] font-semibold">
                      <span className="text-[#D4A853]">Note:</span> ISA Arborist site visits are a paid service starting at $650.
                    </p>
                    <p className="text-[#64748B] text-xs mt-1">
                      Includes comprehensive tree health assessment and detailed written report.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                  Tell Us About Your Project
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="border-2 border-[#10B981]/30 rounded-none focus:border-[#10B981] focus-visible:ring-0 min-h-[100px] bg-[#F8F6F1]/50"
                  placeholder="Describe your tree care needs..."
                  data-testid="contact-message-textarea"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-[#0A1628] mb-2">
                  Upload Photos (Optional - Max 5)
                </label>
                <div
                  className="upload-zone"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="contact-upload-zone"
                >
                  <Upload className="w-8 h-8 text-[#10B981] mx-auto mb-2" />
                  <p className="text-[#0A1628] text-sm font-medium">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-[#64748B] text-xs mt-1">
                    PNG, JPG up to 5MB each
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageUpload}
                  data-testid="contact-file-input"
                />

                {/* Image Previews */}
                {images.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-4">
                    {images.map((img, index) => (
                      <div key={index} className="relative">
                        <img
                          src={img.preview}
                          alt={img.name}
                          className="image-preview"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-[#DC2626] text-white flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white rounded-none py-5 uppercase tracking-[0.15em] text-sm font-bold disabled:opacity-50 shadow-lg shadow-emerald-500/20"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? "Submitting..." : "Request Free Assessment"}
              </Button>

              <p className="text-center text-[#64748B] text-xs">
                By submitting, you agree to receive calls/texts from Celebrity
                Tree Service. We respect your privacy.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
